import { readable, writable, derived } from "svelte/store";
import { cubicOut } from "svelte/easing";

import chroma from "chroma-js";

import { platform, breakpoint } from "./MediaQuery.svelte";

// ///////////////////////////////////////////////////////////
// Constants
// ///////////////////////////////////////////////////////////

export const animationParameters = readable({ delay: 250, duration: 1000, easing: cubicOut });

export const colorsIncoming = readable({
	domestic_production: "#4455F0",
	imports_russia: "#936DFF",
	imports_eu: "#1EB2F1",
	imports_lng: "#28B2BB",
	main: "#4455F0",
});

export const colorsOutgoing = readable({
	households: "#EA4815",
	commerce: "#FF7373",
	industry: "#D71B3D",
	other: "#FF9466",
	main: "#F26546",
});

export const colorsResults = readable({
	// verygood: "#9CF49A",
	verygood: "#85E576",
	good: "#CEE443",
	// neutral: "#F3BE36",
	neutral: "#FFC83C",
	bad: "#FF993A",
	// verybad: "#CB4B2F",
	verybad: "#CB4B2F",
});

export const variableLabels = readable({
	domestic_production: "eigene Erzeugung",
	imports_russia: "Importe Russland",
	imports_eu: "Importe Europa",
	imports_lng: "Importe LNG",
	households: "Haushalte",
	commerce: "Gewerbe",
	industry: "Industrie",
	other: "Sonstige",
});

export const colors = derived(
	[colorsIncoming, colorsOutgoing, colorsResults],
	([colorsIncoming, colorsOutgoing, colorsResults]) => {
		const returnObj = {};
		returnObj.incoming = colorsIncoming;
		returnObj.outgoing = colorsOutgoing;
		returnObj.results = colorsResults;
		return returnObj;
	}
);

export const inOutSectionsBase = readable([
	{ id: "incoming", title: "Lieferungen", subtitle: "in der Heizperiode", unit: "TWh" },
	{ id: "outgoing", title: "Verbrauch", subtitle: "in der Heizperiode", unit: "TWh" },
]);

export const inOutSections = derived([inOutSectionsBase, colors], ([inOutSectionsBase, colors]) =>
	inOutSectionsBase.map((s) => ({ ...s, color: colors[s.id].main }))
);

export const inOutSectionsById = derived(inOutSections, (inOutSections) =>
	inOutSections.reduce((obj, item) => ({ ...obj, [item.id]: item }), {})
);

export const parameterSectionsBase = readable([
	{
		id: "incoming",
		label: "Lieferung",
		icon: "images/lieferungen.svg",
		color: "#5868F9",
		infobox:
			"Hier bestimmen Sie, wie viel <span class='mark'>Gaslieferungen</span> Deutschland erhalten könnte. Ab dem ausgewählten Monat fließen <span class='mark'>keine Importe aus Russland</span> mehr ein. Bei <span class='mark'>zusätzlichen Lieferungen</span> aus anderen Ländern geht das Modell von drei Szenarien aus: Ähnliche Liefermengen wie in Vorjahren, zusätzliche realistische Importe und die maximal möglichen Lieferkapazitäten.",
	},
	{
		id: "outgoing",
		label: "Verbrauch",
		icon: "images/verbrauch.svg",
		color: "#F26546",
		infobox:
			"Hier bestimmen Sie Faktoren, die den <span class='mark'>Gasverbrauch</span> beeinflussen. Der durchschnittliche <span class='mark'>Winter</span> entspricht etwa dem Gasverbrauch von 2021, die Temperatur lag um die 2°C. Im warmem Winter mit einer Durchschnittstemperatur von etwa 4°C Grad wird angenommen, das ca. 10% weniger Gas verbraucht würde. Für einen kalten Winter (durchschnittlich -1°C) werden 10% mehr Verbrauch angenommen. Heizen alle <span class='mark'>Haushalte</span> etwa 1°C weniger, so brächte das rund 6% Ersparnis. Es wird nur der Verbrauch fürs Heizen berücksichtigt. Die Einsparungen in der <span class='mark'>Industrie</span> entsprechen den Einsparungseinschätzungen des Verbands BDEW. 8% gilt als möglich ohne große wirtschaftliche Schäden. 16% ist nach Angaben von Experten möglich, könnte aber zu Einbußen führen. Beim <span class='mark'>Gewerbe</span> gelten laut Bdew 10% als machbar, 20% als Einsparung mit Einbußen.",
	},
]);

export const parameterSections = derived(
	[parameterSectionsBase, colors],
	([parameterSectionsBase, colors]) =>
		parameterSectionsBase.map((s) => ({ ...s, color: colors[s.id].main }))
);

export const parameterSectionsById = derived(parameterSections, (parameterSections) =>
	parameterSections.reduce((obj, item) => ({ ...obj, [item.id]: item }), {})
);

export const slider = readable({
	id: "slider-month",
	label: "Wann stoppt Russland die Gaszufuhr?",
});

export const resultsSection = readable({
	id: "results",
	label: "Prognose",
	icon: "images/prognose.svg",
	color: "#000",
});

export const parameters = writable([
	{
		id: "gas",
		uiType: "slider",
		dataType: "incoming",
		label: "Erhöhen andere Länder ihre Lieferungen?",
		options: [
			{ label: "nein", value: "gas1", index: 1 },
			{ label: "realistisch-optimistisch", value: "gas2", index: 2 },
			{ label: "Best Case", value: "gas3", index: 3 },
		],
		selected: "gas1",
		selectedSliderValue: [2],
		position: 0,
	},
	{
		id: "winter",
		uiType: "slider",
		dataType: "outgoing",
		label: "Winter",
		sublabel: "Temperatur",
		icon: "images/icons_winter.svg",
		options: [
			{ label: "kalt", value: "winter1", index: 1 },
			{ label: "durchschnittlich", value: "winter2", index: 2 },
			{ label: "warm", value: "winter3", index: 3 },
		],
		selected: "winter2",
		selectedSliderValue: [2],
		position: 1,
	},
	{
		id: "industry",
		uiType: "slider",
		dataType: "outgoing",
		label: "Industrie",
		sublabel: "Einsparungen",
		icon: "images/icons_industrie.svg",
		options: [
			{ label: "keine", value: "industrie1", index: 1 },
			{ label: "-8%", value: "industrie2", index: 2 },
			{ label: "-16%", value: "industrie3", index: 3 },
		],
		selected: "industrie1",
		selectedSliderValue: [1],
		position: 3,
	},
	{
		id: "households",
		uiType: "slider",
		dataType: "outgoing",
		label: "Haushalte",
		sublabel: "Heizeinsparungen",
		icon: "images/icons_haushalte.svg",
		options: [
			{ label: "keine", value: "haushalte1", index: 1 },
			{ label: "-1° C", value: "haushalte2", index: 2 },
			{ label: "-3° C", value: "haushalte3", index: 3 },
		],
		selected: "haushalte1",
		selectedSliderValue: [1],
		position: 2,
	},
	{
		id: "commerce",
		uiType: "slider",
		dataType: "outgoing",
		label: "Gewerbe",
		sublabel: "Einsparungen",
		icon: "images/icons_gewerbe.svg",
		options: [
			{ label: "keine", value: "ghd1", index: 1 },
			{ label: "-10%", value: "ghd2", index: 2 },
			{ label: "-20%", value: "ghd3", index: 3 },
		],
		selected: "ghd1",
		selectedSliderValue: [1],
		position: 4,
	},
]);

export const parametersById = derived(parameters, (parameters) => {
	return parameters.reduce((obj, item) => ({ ...obj, [item.id]: item }), {});
});

export const results = derived(colorsResults, (colorsResults) => {
	return {
		verybad: {
			id: "verybad",
			icon: "images/stufe5.svg",
			label:
				"Wahrscheinlich gibt es eine Gaslücke. Produk&shy;tions&shy;stopps und starke Einsparungen wären die Folge.",
			color: colorsResults.verybad,
		},
		// bad: {
		// 	id: "bad",
		// 	icon: "images/stufe4.svg",
		// 	label: "Mini GASLÜCKE!!!!",
		// 	color: colorsResults.bad,
		// },
		neutral: {
			id: "neutral",
			icon: "images/stufe3.svg",
			label: "Es gäbe weniger Gas als im Vorjahr, aber wahrscheinlich keine Gaslücke.",
			color: colorsResults.neutral,
		},
		// good: {
		// 	id: "good",
		// 	icon: "images/stufe2.svg",
		// 	label: "Läuft!",
		// 	color: colorsResults.good,
		// },
		verygood: {
			id: "verygood",
			icon: "images/stufe1.svg",
			label: "Im Winter wäre mehr Gas verfügbar als im Vorjahres&shy;zeitraum.",
			color: colorsResults.verygood,
		},
	};
});

export const monthsBase = readable([
	// { year: "2022", month: 7, name: "Juli", nameShort: "Jul", index: 0 },
	{ year: "2022", month: 8, name: "August", nameShort: "Aug", index: 0 },
	{ year: "2022", month: 9, name: "September", nameShort: "Sep", index: 1 },
	{ year: "2022", month: 10, name: "Oktober", nameShort: "Okt", index: 2 },
	{ year: "2022", month: 11, name: "November", nameShort: "Nov", index: 3 },
	{ year: "2022", month: 12, name: "Dezember", nameShort: "Dez", index: 4 },
	{ year: "2023", month: 1, name: "Januar", nameShort: "Jan", index: 5 },
	{ year: "2023", month: 2, name: "Februar", nameShort: "Feb", index: 6 },
	{ year: "2023", month: 3, name: "März", nameShort: "Mär", index: 7 },
	{ year: "2023", month: 4, name: "April", nameShort: "Apr", index: 8 },
	{ year: "2023", month: 5, name: "Mai", nameShort: "Mai", index: 9 },
	{ year: "2023", month: 6, name: "Juni", nameShort: "Jun", index: 10 },
	{ year: "2023", month: 7, name: "Juli", nameShort: "Jul", index: 11 },
	// { year: "2023", month: 8, name: "August", nameShort: "Aug", index: 12 },
	// { year: "2023", month: 9, name: "September", nameShort: "Sep", index: 14 },
	// { year: "2023", month: 10, name: "Oktober", nameShort: "Okt", index: 15 },
	// { year: "2023", month: 11, name: "November", nameShort: "Nov", index: 16 },
	// { year: "2023", month: 12, name: "Dezember", nameShort: "Dez", index: 17 },
	{ year: "9999", month: 99, name: "kein Gasstopp", nameShort: "kein Gasstopp", index: 99 },
]);

export const nonValueKeys = readable(["month", "year", "size", "date"]);

export const chartOrientation = readable("horizontal");

export const gasStopIndicator = writable(true);

// ///////////////////////////////////////////////////////////
// Input - UI variables
// ///////////////////////////////////////////////////////////

export const currentLowestPoint = writable({});

export const gasStopSliderValues = writable([3]);

export const selectedParameters = derived(parameters, (parameters) => {
	const returnArr = [];
	parameters.forEach((p) => {
		const selectedIndex = p.selectedSliderValue[0];
		position: 0;
		const selectedOption = p.options.filter((o) => o.index === selectedIndex)[0];
		const selectedValue = selectedOption.value;
		returnArr.push(selectedValue);
	});
	return returnArr;
});

export const months = derived(
	[monthsBase, gasStopSliderValues],
	([monthsBase, gasStopSliderValues]) =>
		monthsBase.map((m) => {
			return {
				...m,
				date: new Date(m.year, m.month - 1),
				gasDelivered: !(m.index >= gasStopSliderValues[0]),
			};
		})
);

export const currentLowestPointMonth = derived(
	[currentLowestPoint, months],
	([currentLowestPoint, months]) => months.filter((m) => +m.date === +currentLowestPoint.date)[0]
);

export const noGasMonths = derived(months, (months) => months.filter((m) => !m.gasDelivered));

export const gasStopFirst = derived(noGasMonths, (noGasMonths) => noGasMonths[0]);
export const gasStopLast = derived(noGasMonths, (noGasMonths) => noGasMonths[1]);
export const gasRestart = readable(null);

// ///////////////////////////////////////////////////////////
// Output - UI variables
// ///////////////////////////////////////////////////////////

export const selectedSliderValues = writable([2, 9]);

export const selectedMonths = derived(
	[selectedSliderValues, months],
	([selectedSliderValues, months]) =>
		months.filter((m) => m.index >= selectedSliderValues[0] && m.index < selectedSliderValues[1])
);
